import Checkbox from 'antd/es/checkbox';
import Collapse from 'antd/es/collapse';
import Empty from 'antd/es/empty';
import Input from 'antd/es/input';
import Space from 'antd/es/space';
import Typography from 'antd/es/typography';
import React, { useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import {
  type AvailableToolType,
  type ToolCategory,
  type ToolConfig,
  getToolsByCategory,
} from '@mai/types';

import { ToolContainer, CategoryHeader, ToolItem, ToolName, ToolDescription, SearchContainer, SelectedCount } from './ToolSelector.styles';
import { categories, categoryLabels, filterToolsBySearch, getCategorySelectionState } from './ToolSelector.utils';

const { Search } = Input;
const { Text } = Typography;
const { Panel } = Collapse;

interface ToolSelectorProps {
  value?: ToolConfig;
  onChange?: (value: ToolConfig) => void;
}

export const ToolSelector: React.FC<ToolSelectorProps> = ({
  value = [],
  onChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const [expandedCategories, setExpandedCategories] = useState<string[]>([
    'utilities',
  ]);

  // Convert value array to Set for faster lookups
  const selectedTools = useMemo(() => {
    return new Set(value.map((tool) => tool.type));
  }, [value]);

  const handleToolToggle = (toolType: AvailableToolType, checked: boolean) => {
    let newValue: ToolConfig;

    if (checked) {
      // Add tool
      newValue = [...value, { type: toolType }];
    } else {
      // Remove tool
      newValue = value.filter((tool) => tool.type !== toolType);
    }

    onChange?.(newValue);
  };

  const handleCategoryToggle = (category: ToolCategory, checked: boolean) => {
    const categoryTools = getToolsByCategory(category);
    let newValue = [...value];

    if (checked) {
      // Add all tools from category that aren't already selected
      categoryTools.forEach((tool) => {
        if (!selectedTools.has(tool.type)) {
          newValue.push({ type: tool.type });
        }
      });
    } else {
      // Remove all tools from category
      const categoryToolTypes = new Set(categoryTools.map((t) => t.type));
      newValue = newValue.filter((tool) => !categoryToolTypes.has(tool.type));
    }

    onChange?.(newValue);
  };

  // Filter tools based on search
  const filteredCategories = useMemo(() => {
    return filterToolsBySearch(searchTerm);
  }, [searchTerm]);

  const hasResults = Object.values(filteredCategories).some(
    (tools) => tools.length > 0,
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <SearchContainer>
        <Search
          placeholder="Search tools..."
          allowClear
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '100%' }}
        />
      </SearchContainer>

      <ToolContainer>
        {!hasResults ? (
          <Empty
            description="No tools found"
            style={{ padding: `${theme.paddingMD * 2}px 0` }}
          />
        ) : (
          <Collapse
            ghost
            expandIconPosition="end"
            activeKey={expandedCategories}
            onChange={setExpandedCategories}
          >
            {categories.map((category) => {
              const tools = filteredCategories[category];
              if (tools.length === 0) return null;

              const { categorySelectedCount, allSelected, someSelected } = getCategorySelectionState(tools, selectedTools);

              return (
                <Panel
                  key={category}
                  header={
                    <CategoryHeader>
                      <Space>
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={(e) =>
                            handleCategoryToggle(category, e.target.checked)
                          }
                          onClick={(e) => e.stopPropagation()}
                        >
                          {categoryLabels[category]}
                        </Checkbox>
                        {categorySelectedCount > 0 && (
                          <SelectedCount color="blue">
                            {categorySelectedCount} selected
                          </SelectedCount>
                        )}
                      </Space>
                    </CategoryHeader>
                  }
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {tools.map((tool) => (
                      <ToolItem key={tool.type}>
                        <Checkbox
                          checked={selectedTools.has(tool.type)}
                          onChange={(e) =>
                            handleToolToggle(tool.type, e.target.checked)
                          }
                        >
                          <ToolName>{tool.name}</ToolName>
                        </Checkbox>
                        <ToolDescription>{tool.description}</ToolDescription>
                      </ToolItem>
                    ))}
                  </Space>
                </Panel>
              );
            })}
          </Collapse>
        )}
      </ToolContainer>

      <div style={{ textAlign: 'right' }}>
        <Text type="secondary">
          {selectedTools.size} tool{selectedTools.size !== 1 ? 's' : ''}{' '}
          selected
        </Text>
      </div>
    </Space>
  );
};
